import { useState, useEffect, useCallback } from 'react'
import { useParams } from 'react-router-dom'
import {
  CheckSquare,
  Square,
  CheckCircle,
  Clock,
  Filter,
  Download,
  Trash2,
  RefreshCw,
  AlertCircle,
  X,
  ChevronDown,
  ChevronUp,
  FileText,
} from 'lucide-react'
import * as api from '../services/api'
import type { PostImportTask, PostImportSummary } from '../types'

const categoryColors: Record<string, string> = {
  formulas: 'blue',
  formatting: 'purple',
  cross_sheet: 'cyan',
  automation: 'amber',
  permissions: 'green',
  other: 'gray',
}

function getStatusIcon(status: string) {
  switch (status) {
    case 'completed':
      return <CheckCircle className="w-5 h-5 text-emerald-400" />
    case 'in_progress':
      return <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />
    case 'pending':
    default:
      return <Clock className="w-5 h-5 text-slate-500" />
  }
}

function TaskCard({
  task,
  onToggle,
  onDelete,
  onUpdateNotes,
}: {
  task: PostImportTask
  onToggle: (taskId: string, currentStatus: string) => void
  onDelete: (taskId: string) => void
  onUpdateNotes: (taskId: string, notes: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const [notes, setNotes] = useState(task.notes || '')
  const [editingNotes, setEditingNotes] = useState(false)
  const color = categoryColors[task.category] || 'gray'

  const handleSaveNotes = () => {
    onUpdateNotes(task.id, notes)
    setEditingNotes(false)
  }

  return (
    <div className={`card transition-all ${task.status === 'completed' ? 'opacity-60' : ''}`}>
      <div className="flex items-start gap-4">
        <button onClick={() => onToggle(task.id, task.status)} className="mt-1 flex-shrink-0">
          {task.status === 'completed' ? (
            <CheckSquare className="w-5 h-5 text-emerald-400" />
          ) : (
            <Square className="w-5 h-5 text-slate-500 hover:text-blue-400 transition-colors" />
          )}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h3
                className={`font-medium ${
                  task.status === 'completed' ? 'text-slate-500 line-through' : 'text-slate-200'
                }`}
              >
                {task.title}
              </h3>
              <div className="flex items-center gap-2 mt-1">
                <span className={`badge-${color}`}>{task.category}</span>
                {task.sheet_id && (
                  <span className="text-xs text-slate-500">Sheet: {task.sheet_id}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusIcon(task.status)}
              <button onClick={() => setExpanded(!expanded)} className="btn-ghost btn-icon btn-sm">
                {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              </button>
            </div>
          </div>

          {expanded && (
            <div className="mt-4 space-y-3">
              <p className="text-sm text-slate-400">{task.description}</p>

              {task.steps && task.steps.length > 0 && (
                <div className="p-3 bg-slate-800/50 rounded-lg">
                  <p className="text-xs text-slate-500 mb-1">Steps</p>
                  <ol className="text-sm text-slate-300 list-decimal list-inside space-y-1">
                    {task.steps.map((step, idx) => (
                      <li key={idx}>{step}</li>
                    ))}
                  </ol>
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-1">
                  <p className="text-xs text-slate-500">Notes</p>
                  {!editingNotes && (
                    <button
                      onClick={() => setEditingNotes(true)}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      Edit
                    </button>
                  )}
                </div>
                {editingNotes ? (
                  <div className="space-y-2">
                    <textarea
                      value={notes}
                      onChange={(e) => setNotes(e.target.value)}
                      className="textarea text-sm"
                      rows={2}
                      placeholder="Add notes..."
                    />
                    <div className="flex gap-2">
                      <button onClick={handleSaveNotes} className="btn-primary btn-sm">
                        Save
                      </button>
                      <button
                        onClick={() => {
                          setNotes(task.notes || '')
                          setEditingNotes(false)
                        }}
                        className="btn-ghost btn-sm"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="text-sm text-slate-400">{task.notes || 'No notes'}</p>
                )}
              </div>

              <div className="flex items-center justify-between pt-2 border-t border-slate-700/50">
                <p className="text-xs text-slate-500">
                  {task.completed_at
                    ? `Completed ${new Date(task.completed_at).toLocaleDateString()}`
                    : `Created ${new Date(task.created_at).toLocaleDateString()}`}
                </p>
                <button
                  onClick={() => onDelete(task.id)}
                  className="btn-ghost btn-icon btn-sm text-red-400 hover:text-red-300"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function SummaryCard({ summary }: { summary: PostImportSummary }) {
  const completedTasks = summary.by_status.completed || 0
  const pendingTasks = summary.by_status.pending || 0
  const completionPercent = summary.completion_percentage

  return (
    <div className="card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold text-slate-100">Progress</h3>
        <span className="text-2xl font-bold gradient-text">{completionPercent}%</span>
      </div>
      <div className="progress-bar mb-4">
        <div className="progress-bar-fill" style={{ width: `${completionPercent}%` }} />
      </div>
      <div className="grid grid-cols-3 gap-4 text-center">
        <div>
          <p className="text-2xl font-bold text-slate-200">{completedTasks}</p>
          <p className="text-xs text-slate-500">Completed</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-200">{pendingTasks}</p>
          <p className="text-xs text-slate-500">Pending</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-slate-200">{summary.total}</p>
          <p className="text-xs text-slate-500">Total</p>
        </div>
      </div>
      {Object.keys(summary.by_category).length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-700/50">
          <p className="text-xs text-slate-500 mb-2">By Category</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(summary.by_category).map(([category, data]) => (
              <span key={category} className={`badge-${categoryColors[category] || 'gray'}`}>
                {category}: {data.completed}/{data.total}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default function PostImportTasks() {
  const { projectId } = useParams<{ projectId: string }>()
  const [tasks, setTasks] = useState<PostImportTask[]>([])
  const [summary, setSummary] = useState<PostImportSummary | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [filterCategory, setFilterCategory] = useState<string>('')
  const [filterStatus, setFilterStatus] = useState<string>('')
  const [markingAll, setMarkingAll] = useState(false)

  const fetchData = useCallback(async () => {
    if (!projectId) return
    setLoading(true)
    setError(null)
    try {
      const [tasksRes, summaryRes] = await Promise.all([
        api.getPostImportTasks(projectId, filterCategory || undefined, filterStatus || undefined),
        api.getPostImportSummary(projectId),
      ])
      setTasks(tasksRes.data)
      setSummary(summaryRes.data)
    } catch (err) {
      console.error('Failed to fetch tasks:', err)
      setError('Failed to load post-import tasks')
    } finally {
      setLoading(false)
    }
  }, [projectId, filterCategory, filterStatus])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleToggleTask = async (taskId: string, currentStatus: string) => {
    if (!projectId) return
    const newStatus = currentStatus === 'completed' ? 'pending' : 'completed'
    try {
      await api.updatePostImportTask(projectId, taskId, newStatus)
      await fetchData()
    } catch (err) {
      console.error('Failed to update task:', err)
      setError('Failed to update task')
    }
  }

  const handleUpdateNotes = async (taskId: string, notes: string) => {
    if (!projectId) return
    try {
      await api.updatePostImportTask(projectId, taskId, undefined, notes)
      await fetchData()
    } catch (err) {
      console.error('Failed to update notes:', err)
      setError('Failed to update notes')
    }
  }

  const handleDeleteTask = async (taskId: string) => {
    if (!projectId) return
    if (!confirm('Delete this task?')) return
    try {
      await api.deletePostImportTask(projectId, taskId)
      await fetchData()
    } catch (err) {
      console.error('Failed to delete task:', err)
      setError('Failed to delete task')
    }
  }

  const handleMarkAllComplete = async () => {
    if (!projectId) return
    if (!confirm('Mark all visible tasks as complete?')) return
    setMarkingAll(true)
    try {
      await api.markAllTasksComplete(projectId, filterCategory || undefined)
      await fetchData()
    } catch (err) {
      console.error('Failed to mark all complete:', err)
      setError('Failed to mark all tasks complete')
    } finally {
      setMarkingAll(false)
    }
  }

  const handleDownloadMarkdown = async () => {
    if (!projectId) return
    try {
      const response = await api.getPostImportMarkdown(projectId)
      await api.downloadPostImportMarkdown(projectId, response.data.project_name)
    } catch (err) {
      console.error('Failed to download:', err)
      setError('Failed to download markdown')
    }
  }

  const categories = [...new Set(tasks.map((t) => t.category))]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Post-Import Tasks</h1>
          <p className="text-slate-400 mt-1">Manual configuration tasks after Smartsheets import</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleDownloadMarkdown} className="btn-secondary">
            <Download className="w-4 h-4" />
            Download Guide
          </button>
          <button
            onClick={handleMarkAllComplete}
            disabled={markingAll || tasks.length === 0}
            className="btn-primary"
          >
            {markingAll ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Marking...
              </>
            ) : (
              <>
                <CheckCircle className="w-4 h-4" />
                Mark All Complete
              </>
            )}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="alert-error">
          <AlertCircle className="w-5 h-5" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Summary + Filters */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {summary && <SummaryCard summary={summary} />}
        <div className="lg:col-span-2 card">
          <h3 className="font-semibold text-slate-100 mb-4">Filters</h3>
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <label className="label">Category</label>
              <div className="relative">
                <Filter className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                <select
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  className="select pl-10"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat} value={cat}>
                      {cat}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex-1">
              <label className="label">Status</label>
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="select"
              >
                <option value="">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Tasks List */}
      {loading ? (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card">
              <div className="flex items-center gap-4">
                <div className="skeleton w-5 h-5 rounded" />
                <div className="flex-1">
                  <div className="skeleton h-5 w-48 mb-2" />
                  <div className="skeleton h-4 w-32" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : tasks.length === 0 ? (
        <div className="card text-center py-12">
          <FileText className="w-16 h-16 text-slate-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-slate-300 mb-2">No post-import tasks</h3>
          <p className="text-slate-500">
            {filterCategory || filterStatus
              ? 'No tasks match your filters'
              : 'Tasks will appear after importing data to Smartsheets'}
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          {tasks.map((task) => (
            <TaskCard
              key={task.id}
              task={task}
              onToggle={handleToggleTask}
              onDelete={handleDeleteTask}
              onUpdateNotes={handleUpdateNotes}
            />
          ))}
        </div>
      )}
    </div>
  )
}
